import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Global search box — queries /search across GPOs, FRS, ARS and POs and shows
// a grouped results dropdown. Clicking a result routes to the owning module.
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  // Debounced search as the user types.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setData(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/search?q=' + encodeURIComponent(q.trim()));
        setData(r.data.data); setOpen(true);
      } catch { setData(null); }
      finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (path) => { setOpen(false); setQ(''); navigate(path); };

  const total = data ? data.gpos.length + data.frs.length + data.ars.length + data.pos.length : 0;

  const Group = ({ title, items, render }) => items.length === 0 ? null : (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 14px 4px' }}>{title} ({items.length})</div>
      {items.map(render)}
    </div>
  );
  const row = (key, primary, secondary, color, onClick) => (
    <div key={key} onClick={onClick} style={{ padding: '7px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}
      onMouseEnter={e => e.currentTarget.style.background = '#f4f5fa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span><span style={{ fontFamily: 'monospace', fontWeight: 700, color }}>{primary}</span> <span style={{ color: '#888', marginLeft: 8 }}>{secondary}</span></span>
    </div>
  );

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => data && setOpen(true)}
        placeholder="🔍 Search GPO / FRS / ARS / PO…"
        style={{ width: 230, padding: '7px 12px', border: '1px solid #ddd', borderRadius: 18, fontSize: 12.5, outline: 'none', background: '#F7F6F3' }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{ position: 'absolute', top: 38, right: 0, width: 360, maxHeight: 440, overflowY: 'auto', background: 'white', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', zIndex: 3000, border: '1px solid #eee' }}>
          {loading && <div style={{ padding: 16, color: '#aaa', fontSize: 13 }}>Searching…</div>}
          {!loading && total === 0 && <div style={{ padding: 16, color: '#aaa', fontSize: 13 }}>No matches for “{q}”.</div>}
          {!loading && data && (
            <div style={{ paddingBottom: 8 }}>
              <Group title="GPOs" items={data.gpos} render={g => row(g.id, g.gpo_no, [g.style_no, g.brand].filter(Boolean).join(' · '), '#3B3A8F', () => go('/order-execution?tab=gpos&open=' + encodeURIComponent(g.gpo_no)))} />
              <Group title="FRS" items={data.frs} render={f => row(f.id, f.frs_no, 'GPO ' + (f.gpo_no || '—') + ' · ' + (f.style_no || ''), '#0891b2', () => go('/order-execution?tab=frs&open=' + encodeURIComponent(f.frs_no)))} />
              <Group title="ARS" items={data.ars} render={a => row(a.id, a.ars_no, 'GPO ' + (a.gpo_no || '—') + ' · ' + (a.style_no || ''), '#854F0B', () => go('/order-execution?tab=ars&open=' + encodeURIComponent(a.ars_no)))} />
              <Group title="Purchase Orders" items={data.pos} render={p => row(p.id, p.po_no, (p.material_type || '') + ' · GPO ' + (p.gpo_no || '—'), '#166534', () => go('/procurement?tab=pos&open=' + encodeURIComponent(p.po_no)))} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
